import React, { useEffect, useState } from 'react';
import { RichTextBlock } from 'prismic-reactjs';
import { graphql, useStaticQuery } from 'gatsby';
import Button from '@components/button';
import {
  BannerBar,
  BannerInner,
  BannerText,
  BannerActions,
} from './global-notification-banner.styled';

interface BannerLinkType {
  url?: string | null;
  target?: string | null;
  link_type?: string | null;
  isBroken?: boolean | null;
}

interface GlobalNotificationBannerQueryData {
  prismicGeneralConfig: {
    data: {
      notification_banner_text?: { richText: RichTextBlock[] } | null;
      notification_banner_link?: BannerLinkType | null;
      notification_banner_link_label?: string | null;
    };
  };
}

const deriveStorageKey = (text: RichTextBlock[], link?: BannerLinkType | null): string => {
  const raw = JSON.stringify(text) + (link?.url ?? '');
  const charSum = Array.from(raw).reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return `global_notification_banner_dismissed_${charSum}_${raw.length}`;
};

const GlobalNotificationBanner = (): JSX.Element | null => {
  const [isDismissed, setIsDismissed] = useState(true);

  const {
    prismicGeneralConfig: {
      data: { notification_banner_text, notification_banner_link, notification_banner_link_label },
    },
  } = useStaticQuery<GlobalNotificationBannerQueryData>(graphql`
    query GlobalNotificationBannerQuery {
      prismicGeneralConfig {
        data {
          notification_banner_text {
            richText
          }
          notification_banner_link {
            url
            target
            link_type
            isBroken
          }
          notification_banner_link_label
        }
      }
    }
  `);

  const bannerText = notification_banner_text?.richText;
  const bannerLink = notification_banner_link;

  const hasContent =
    Array.isArray(bannerText) &&
    bannerText.length > 0 &&
    bannerText.some((block) => block.text && block.text.trim().length > 0);

  const storageKey = hasContent && bannerText ? deriveStorageKey(bannerText, bannerLink) : '';

  useEffect(() => {
    if (!hasContent) return;
    try {
      const dismissed = localStorage.getItem(storageKey) === '1';
      setIsDismissed(dismissed);
    } catch {
      setIsDismissed(false);
    }
  }, [storageKey, hasContent]);

  const handleDismiss = () => {
    try {
      localStorage.setItem(storageKey, '1');
    } catch {
      // noop
    }
    setIsDismissed(true);
  };

  if (!hasContent || isDismissed || !bannerText) return null;

  const hasValidLink =
    bannerLink != null &&
    typeof bannerLink.url === 'string' &&
    bannerLink.url.length > 0 &&
    !bannerLink.isBroken &&
    bannerLink.link_type !== 'Any';

  const linkLabel =
    typeof notification_banner_link_label === 'string' &&
    notification_banner_link_label.trim().length > 0
      ? notification_banner_link_label.trim()
      : 'Learn more';

  const handleLinkClick = () => {
    if (bannerLink?.url) {
      window.open(bannerLink.url, bannerLink.target ?? '_self');
    }
  };

  return (
    <BannerBar role="region" aria-label="Notification banner">
      <BannerInner>
        <BannerText type="description" richText={bannerText} />
        <BannerActions>
          {hasValidLink && (
            <Button
              size="small"
              variant="darkPrismicTheme"
              label={linkLabel}
              onClick={handleLinkClick}
            />
          )}
          <Button
            size="small"
            variant="darkPrismicTheme"
            outlined
            label="&#x2715;"
            aria-label="Dismiss notification"
            onClick={handleDismiss}
          />
        </BannerActions>
      </BannerInner>
    </BannerBar>
  );
};

export default GlobalNotificationBanner;
